import { useMemo, useState } from 'react';
import type { ClientPackage } from '@salonomy/types';
import { Button, Field, Modal, Pill, Select, TextInput } from '@salonomy/ui';
import { useClientPackages, useRedeemPackage } from '../../data/packages.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from './money.js';
import './till.css';

/**
 * WS-13 Feature 3a — REDEEM a prepaid package at checkout, beside VoucherRedeemModal. Pick one of the
 * client's ACTIVE packages → show the SERVER's DERIVED remaining (sessions and value — never recomputed
 * here) → redeem against the basket. A redemption recognises revenue + VAT on the real serviced line and
 * decrements the package liability (ADR-0009); it is a TENDER against the supply, never a discount line.
 *
 * HONESTY (non-negotiable): every number shown is a SERVER number. `remainingSessions` / `remainingValuePence`
 * are the signed sum of the ledger the API computed; the redeemable VALUE cap is `min(remainingValue, dueNow)`
 * (both server values). For a prepaid_sessions package the till redeems whole sessions (default 1) and the
 * redeemed line value defaults to the due amount; for a prepaid_value package it redeems a value amount. The
 * UI never recomputes a remaining or a basket total — after a successful redeem the caller records the
 * server-returned amount. Redeem is idempotent on a per-intent key minted once on submit; the button is
 * disabled while pending so a double-tap can't redeem twice, and a 422/409 surfaces faithfully.
 */
export function RedeemPackageModal({
  appointmentId,
  clientId,
  serviceName,
  dueNowPence,
  onRedeemed,
  onClose,
}: {
  appointmentId: string;
  clientId: string | null;
  /** A description for the redeemed line — the supply the package is tendered against. */
  serviceName: string;
  /** The basket's outstanding amount (a server number from the appointment), the redeemable value cap. */
  dueNowPence: number;
  /** Called with the server-confirmed amount redeemed (pence) so the till records the tender. */
  onRedeemed: (amountPence: number) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const redeem = useRedeemPackage();
  const { data: packages, isFetching } = useClientPackages(clientId, 'active');

  // Only packages with something left to redeem (value remaining, or — for a session package — sessions).
  const redeemable = useMemo<ClientPackage[]>(
    () =>
      (packages ?? []).filter(
        (p) =>
          p.remainingValuePence > 0 && (p.remainingSessions == null || p.remainingSessions > 0),
      ),
    [packages],
  );

  const [packageId, setPackageId] = useState('');
  const selected = redeemable.find((p) => p.id === packageId) ?? null;

  const noClient = clientId == null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Redeem package"
      width={460}
      footer={
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      {noClient ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          This checkout has no client on it, so there are no purchased packages to redeem. Add a
          client to the booking first.
        </div>
      ) : isFetching && redeemable.length === 0 ? (
        <div className="muted" style={{ fontSize: 13.5 }}>
          Loading this client&rsquo;s packages…
        </div>
      ) : redeemable.length === 0 ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          This client has no active package with anything left to redeem. Sell them one from the
          till or check their record.
        </div>
      ) : (
        <>
          <Field label="Package">
            <Select value={packageId} onChange={(e) => setPackageId(e.target.value)}>
              <option value="">Choose a package…</option>
              {redeemable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.remainingSessions != null
                    ? ` · ${p.remainingSessions} session${p.remainingSessions === 1 ? '' : 's'} left`
                    : ` · ${money(p.remainingValuePence)} left`}
                </option>
              ))}
            </Select>
          </Field>

          {selected ? (
            <RedeemPanel
              key={selected.id}
              pkg={selected}
              appointmentId={appointmentId}
              serviceName={serviceName}
              dueNowPence={dueNowPence}
              pending={redeem.isPending}
              onSubmit={async (amountPence, sessions) => {
                try {
                  // Mint the idempotency key once per intent; the button is disabled while pending so a
                  // double-tap can't reach here a second time, and the API is idempotent on this key.
                  const updated = await redeem.mutateAsync({
                    id: selected.id,
                    body: {
                      amountPence,
                      sessions,
                      redeemedLines: [
                        {
                          description: serviceName,
                          qty: 1,
                          unitPricePence: amountPence,
                          serviceId: undefined,
                        },
                      ],
                      appointmentId,
                      idempotencyKey: crypto.randomUUID(),
                    },
                  });
                  // Record the tender from the server's view (the value the liability moved by), then
                  // close. We don't recompute the new remaining here — the hook cached `updated`.
                  onRedeemed(amountPence);
                  const left =
                    updated.remainingSessions != null
                      ? `${updated.remainingSessions} session${updated.remainingSessions === 1 ? '' : 's'} left`
                      : `${money(updated.remainingValuePence)} left`;
                  toast.success(`Redeemed ${money(amountPence)} · ${left}`);
                  onClose();
                } catch (err) {
                  toast.error(
                    err instanceof ApiClientError ? err.message : 'Could not redeem the package',
                  );
                }
              }}
            />
          ) : null}
        </>
      )}
    </Modal>
  );
}

/**
 * The redeem step, shown once a package is chosen. DISPLAYS the server's derived remaining (sessions and/or
 * value). The value cap is `min(remainingValue, dueNow)` — both server numbers — and the amount defaults to
 * the cap (the common "use it against this bill" tap). For a session package the till also redeems whole
 * sessions (default 1, capped at the server's remaining). The server re-checks both on submit.
 */
function RedeemPanel({
  pkg,
  serviceName,
  dueNowPence,
  pending,
  onSubmit,
}: {
  pkg: ClientPackage;
  appointmentId: string;
  serviceName: string;
  dueNowPence: number;
  pending: boolean;
  onSubmit: (amountPence: number, sessions: number) => void;
}) {
  const isSessions = pkg.remainingSessions != null;
  // The value cap is min(remaining value, due now) — both server figures.
  const valueCap = Math.min(pkg.remainingValuePence, dueNowPence);
  const sessionCap = pkg.remainingSessions ?? 1;

  const [amount, setAmount] = useState(penceToPounds(valueCap));
  const [sessions, setSessions] = useState('1');

  const parsedAmount = poundsToPence(amount);
  const parsedSessions = Math.trunc(Number(sessions) || 0);
  const amountValid = parsedAmount != null && parsedAmount > 0 && parsedAmount <= valueCap;
  const sessionsValid = !isSessions || (parsedSessions >= 1 && parsedSessions <= sessionCap);
  const valid = amountValid && sessionsValid;

  return (
    <div className="till-voucher__found" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
        <Pill tone="neutral">{isSessions ? 'Prepaid sessions' : 'Prepaid value'}</Pill>
        {pkg.expiresAt ? <Pill tone="soft">Expires {pkg.expiresAt.slice(0, 10)}</Pill> : null}
      </div>

      {isSessions ? (
        <div className="till__line">
          <span>Sessions remaining</span>
          <span className="mono">{pkg.remainingSessions}</span>
        </div>
      ) : null}
      <div className="till__line">
        <span>Value remaining</span>
        <span className="mono">{money(pkg.remainingValuePence)}</span>
      </div>
      <div className="till__line">
        <span>Due now</span>
        <span className="mono">{money(dueNowPence)}</span>
      </div>
      <div className="till__line till__line--total">
        <span>Can tender up to</span>
        <span className="mono">{money(valueCap)}</span>
      </div>

      {isSessions ? (
        <Field
          label="Sessions to redeem"
          hint={`Up to ${sessionCap} remaining.`}
          error={parsedSessions > sessionCap ? `Only ${sessionCap} session(s) remaining.` : null}
        >
          <TextInput
            inputMode="numeric"
            value={sessions}
            onChange={(e) => setSessions(e.target.value)}
            placeholder="1"
          />
        </Field>
      ) : null}

      <Field
        label="Amount to redeem (£)"
        hint={`Up to ${money(valueCap)}. Applied as a tender against ${serviceName}.`}
        error={
          parsedAmount != null && parsedAmount > valueCap
            ? `Can’t redeem more than ${money(valueCap)}.`
            : null
        }
      >
        <TextInput
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />
      </Field>

      <Button
        block
        size="lg"
        disabled={!valid || pending}
        onClick={() => {
          if (valid && parsedAmount != null) {
            onSubmit(parsedAmount, isSessions ? parsedSessions : 1);
          }
        }}
      >
        {pending
          ? 'Redeeming…'
          : `Redeem ${parsedAmount != null && valid ? money(parsedAmount) : ''}`}
      </Button>
    </div>
  );
}
